import { Card } from '../../../shared/ui/Primitives';
import type { Page01Snapshot } from '../adapters/demo-reader';

const point = (index: number, value: number) => {
  const angle = (Math.PI * index) / 3 - Math.PI / 2;
  return [210 + Math.cos(angle) * value, 146 + Math.sin(angle) * value] as const;
};
export function CapabilityChart({ snapshot }: { snapshot: Page01Snapshot }) {
  const { capability } = snapshot.data;
  const unknown = snapshot.assessment === 'unknown';
  return (
    <Card zone="Z07" title="能力覆盖概览">
      <div className="p01-chart-tabs" aria-label="能力分类">
        {['综合', '人员能力', '车辆装备', '专业力量', '航空应急'].map((name, i) => (
          <button
            key={name}
            disabled={i !== 0}
            aria-pressed={i === 0}
            title={i ? '未提供该分类示例数据' : '综合示例'}
          >
            {name}
          </button>
        ))}
      </div>
      <p className="p01-muted p01-chart-note">其他分类未提供示例数据</p>
      {unknown ? (
        <div className="p01-empty">
          <strong>—</strong>
          <p>待评估 · 尚未获得可用评估资料</p>
        </div>
      ) : (
        <svg
          className="p01-radar"
          viewBox="0 0 420 300"
          role="img"
          aria-label={`六维能力示例：${capability.map((item) => `${item.name}${item.value}%`).join('，')}`}
        >
          {[20, 40, 60, 80, 100].map((radius) => (
            <polygon
              key={radius}
              points={capability.map((_, i) => point(i, radius).join(',')).join(' ')}
              fill="none"
              stroke="#dceafa"
            />
          ))}
          {capability.map((_, i) => {
            const end = point(i, 100);
            return <path key={i} d={`M210 146L${end[0]} ${end[1]}`} stroke="#dceafa" />;
          })}
          <polygon
            points={capability.map((item, i) => point(i, item.value).join(',')).join(' ')}
            fill="#2584ff26"
            stroke="#216bff"
            strokeWidth="2.5"
          />
          {capability.map((item, i) => {
            const p = point(i, item.value);
            const label = point(i, 126);
            return (
              <g key={item.name}>
                <circle cx={p[0]} cy={p[1]} r="4" fill="#216bff" stroke="white" />
                <text
                  x={label[0]}
                  y={label[1] - 5}
                  textAnchor={i === 0 || i === 3 ? 'middle' : i < 3 ? 'start' : 'end'}
                >
                  {item.name}
                  <tspan x={label[0]} dy="19" className="p01-chart-value">
                    {item.value}%
                  </tspan>
                </text>
              </g>
            );
          })}
        </svg>
      )}
      <p className="p01-muted">● 能力维度示例，非场景建设进度。</p>
    </Card>
  );
}
export function TrendChart({ snapshot }: { snapshot: Page01Snapshot }) {
  const trend = snapshot.data.trend;
  const points = trend.values.map((value, i) => `${42 + i * 69},${220 - value * 4.5}`).join(' ');
  return (
    <Card zone="Z11" title="风险趋势">
      <div className="p01-trend-heading">
        <strong>{trend.metric}</strong>
        <div className="p01-chart-tabs">
          <button aria-pressed="true">近7天</button>
          <button disabled title="未提供近30天示例数据">
            近30天
          </button>
          <button disabled title="未提供近3个月示例数据">
            近3个月
          </button>
        </div>
      </div>
      <p className="p01-muted">{trend.range}</p>
      <svg
        viewBox="0 0 480 255"
        className="p01-trend"
        role="img"
        aria-label={`事件数量（起），${trend.labels.map((label, i) => `${label}：${trend.values[i]}`).join('，')}`}
      >
        <defs>
          <linearGradient id="p01-area" x2="0" y2="1">
            <stop stopColor="#3388ff" stopOpacity=".25" />
            <stop offset="1" stopColor="#3388ff" stopOpacity=".02" />
          </linearGradient>
        </defs>
        {[0, 10, 20, 30, 40].map((value) => (
          <g key={value}>
            <path d={`M42 ${220 - value * 4.5}H456`} stroke="#e7eff8" />
            <text x="32" y={225 - value * 4.5} textAnchor="end">
              {value}
            </text>
          </g>
        ))}
        {trend.labels.map((label, i) => (
          <g key={label}>
            <path d={`M${42 + i * 69} 40V220`} stroke="#edf3fa" />
            <text x={42 + i * 69} y="245" textAnchor="middle">
              {label}
            </text>
          </g>
        ))}
        <polygon points={`42,220 ${points} 456,220`} fill="url(#p01-area)" />
        <polyline points={points} fill="none" stroke="#216bff" strokeWidth="2.5" />
        {trend.values.map((value, i) => (
          <circle
            key={i}
            cx={42 + i * 69}
            cy={220 - value * 4.5}
            r="4.5"
            fill="#216bff"
            stroke="white"
          >
            <title>
              {trend.labels[i]}：{value}起（示例）
            </title>
          </circle>
        ))}
      </svg>
      <p className="p01-muted">近30天、近3个月未提供示例数据。</p>
      <details className="p01-data-note">
        <summary>示例数据说明</summary>
        <p>{trend.note}</p>
      </details>
    </Card>
  );
}
